import React, { useState, useEffect, useRef, useContext, useMemo, useCallback } from "react";
import { Button, Spinner, Modal, Row, Col, Form } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Swal from "sweetalert2";
import {
  IconPlus,
  IconEdit,
  IconTrash,
  IconCheck,
  IconClock,
  IconCalendarEvent,
  IconPalette,
  IconToggleLeft,
  IconToggleRight,
  IconSearch,
  IconSettings,
  IconAlertCircle,
  IconFilter,
} from "@tabler/icons-react";
import { SketchPicker } from "react-color";
import appointmentTypesService from "../../services/AppointmentTypesService";
import DatatablePaginated from "../../jsx/components/DatatablePaginated";
import "./AppointmentTypesManager.css";
import "./AppointmentTypesManager.mobile.css";
import NumberFormat from "react-number-format";
import UserContext from "../../contexts/UserContext";
import {
  ColorButton,
  ColorInputContainer,
  ColorPickerContainer,
  CustomSwitch,
  ErrorMessage,
  HelpText,
  InfoCard,
  Input,
  Label,
  LoadingContainer,
  SearchContainer,
  SearchIcon,
  SearchInput,
  SwitchContainer,
} from "./styles";

// Schema para validação dos tipos de atendimento
const appointmentTypeSchema = yup.object().shape({
  name: yup.string().required("Nome é obrigatório"),
  duration: yup
    .number()
    .required("Duração é obrigatória")
    .positive("Duração deve ser um número positivo")
    .integer("Duração deve ser um número inteiro"),
  description: yup.string(),
  price: yup.string(),
  color: yup.string().required("Cor é obrigatória"),
  is_active: yup.boolean().default(true),
  calendar: yup.number().required("Agenda é obrigatório"),
});

// Schema para validação das configurações globais
const settingsSchema = yup.object().shape({
  appointment_duration: yup
    .number()
    .required("Duração padrão é obrigatória")
    .positive("Duração deve ser um número positivo")
    .integer("Duração deve ser um número inteiro"),
  appointment_interval: yup
    .number()
    .required("Intervalo é obrigatório")
    .min(0, "Intervalo deve ser zero ou positivo")
    .integer("Intervalo deve ser um número inteiro"),
});

const AppointmentTypesManager = () => {
  const { user } = useContext(UserContext);
  const [loading, setLoading] = useState(false);
  const [appointmentTypes, setAppointmentTypes] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [settings, setSettings] = useState(null);
  const [calendars, setCalendars] = useState([]);
  
  // Estados para paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(20);
  
  // Estados para filtros
  const [filterStatus, setFilterStatus] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  
  const pickerRef = useRef(null);

  // Form para tipo de atendimento
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    control,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(appointmentTypeSchema),
    defaultValues: {
      name: "",
      duration: 30,
      description: "",
      price: "",
      color: "#137ff7",
      is_active: true,
      calendar: null,
    },
  });

  // Form para configurações globais
  const {
    register: registerSettings,
    handleSubmit: handleSubmitSettings,
    reset: resetSettings,
    formState: { errors: errorsSettings },
  } = useForm({
    resolver: yupResolver(settingsSchema),
    defaultValues: {
      appointment_duration: 30,
      appointment_interval: 0,
    },
  });

  const watchedColor = watch("color");
  const watchedIsActive = watch("is_active");

  // Carregar dados iniciais
  useEffect(() => {
    fetchAppointmentTypes(1, 20);
    fetchSettings();
    fetchCalendars();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Detecta cliques fora do picker
  useEffect(() => {
    function handleClickOutside(event) {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setShowColorPicker(false);
      }
    }

    if (showColorPicker) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showColorPicker]);

  // Buscar tipos de atendimento com paginação
  const fetchAppointmentTypes = useCallback(async (
    page = 1,
    pageSize = 20,
    statusFilter = "all",
    search = ""
  ) => {
    setLoading(true);
    try {
      const params = {
        page: page || 1,
        page_size: pageSize || 20,
      };

      // Adicionar filtro de status
      const currentStatusFilter = statusFilter || filterStatus || "all";
      if (currentStatusFilter && currentStatusFilter !== "all") {
        params.is_active = currentStatusFilter === "active";
      }

      // Adicionar busca
      const currentSearch = search || searchTerm || "";
      if (currentSearch && currentSearch.trim() !== "") {
        params.search = currentSearch.trim();
      }

      const response = await appointmentTypesService.fetchAppointmentTypes(params);
      
      setAppointmentTypes(response.results || []);
      setTotalRows(response.count || 0);
      setCurrentPage(response.current_page || 1);
    } catch (error) {
      console.error("Erro ao carregar tipos de atendimento:", error);
      Swal.fire({
        title: "Erro",
        text: "Não foi possível carregar os tipos de atendimento",
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [filterStatus, searchTerm]);

  // Reagir às mudanças de filtros
  useEffect(() => {
    if (filterStatus !== undefined && searchTerm !== undefined) {
      setCurrentPage(1); // Reset para primeira página quando filtros mudam
      fetchAppointmentTypes(1, perPage, filterStatus, searchTerm);
    }
  }, [filterStatus, searchTerm, perPage, fetchAppointmentTypes]);

  // Buscar configurações globais
  const fetchSettings = async () => {
    try {
      const settingsData = await appointmentTypesService.fetchSettings(
        user?.account_id
      );
      setSettings(settingsData);

      if (settingsData) {
        resetSettings({
          appointment_duration: settingsData.appointment_duration,
          appointment_interval: settingsData.appointment_interval || 0,
        });
      }
    } catch (error) {
      // Erro já é tratado no service
      if (error.response?.status !== 404) {
        console.warn("Configurações não encontradas, usando valores padrão");
      }
    }
  };

  // Buscar agendas disponíveis
  const fetchCalendars = async () => {
    try {
      const calendarList = await appointmentTypesService.fetchCalendars();
      setCalendars(calendarList);
    } catch (error) {
      console.error("Erro ao carregar agendas:", error);
    }
  };

  // Salvar configurações globais
  const onSubmitSettings = async (data) => {
    setLoading(true);
    try {
      await appointmentTypesService.saveSettings(
        data,
        user?.account_id,
        settings?.id
      );

      Swal.fire({
        title: "Sucesso",
        text: "Configurações salvas com sucesso",
        icon: "success",
      });

      setShowSettingsModal(false);
      fetchSettings();
    } catch (error) {
      Swal.fire({
        title: "Erro",
        text: error.response?.data?.detail || "Erro ao salvar configurações",
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Abrir modal de configurações
  const handleOpenSettings = () => {
    if (settings) {
      resetSettings({
        appointment_duration: settings.appointment_duration,
        appointment_interval: settings.appointment_interval || 0,
      });
    }
    setShowSettingsModal(true);
  };

  // Abrir modal para adicionar
  const handleAddNew = () => {
    const defaultCalendar = calendars.length === 1 ? calendars[0].id : null;
    reset({
      name: "",
      duration: settings?.appointment_duration || 30,
      description: "",
      price: "",
      color: "#137ff7",
      is_active: true,
      calendar: defaultCalendar,
    });
    setEditingId(null);
    setShowModal(true);
  };

  // Abrir modal para editar
  const handleEdit = useCallback((type, e) => {
    e.stopPropagation();
    reset({
      name: type.name,
      duration: type.duration,
      description: type.description || "",
      price: type.price ? parseFloat(type.price) : "",
      color: type.color || "#137ff7",
      is_active: type.is_active,
      calendar: type.calendar?.id || null,
    });
    setEditingId(type.id);
    setShowModal(true);
  }, [reset, setEditingId, setShowModal]);

  // Salvar tipo de atendimento
  const onSubmit = async (data) => {
    setLoading(true);
    try {
      await appointmentTypesService.saveAppointmentType(
        data,
        user?.account_id,
        editingId
      );

      Swal.fire({
        title: "Sucesso",
        text: editingId
          ? "Tipo de atendimento atualizado com sucesso"
          : "Tipo de atendimento adicionado com sucesso",
        icon: "success",
      });

      setShowModal(false);
      fetchAppointmentTypes(currentPage, perPage, filterStatus, searchTerm);
    } catch (error) {
      Swal.fire({
        title: "Erro",
        text:
          error.response?.data?.detail || "Erro ao salvar tipo de atendimento",
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Remover tipo de atendimento
  const handleDelete = useCallback(async (id, e) => {
    e.stopPropagation();
    const result = await Swal.fire({
      title: "Confirmar exclusão",
      text: "Tem certeza que deseja excluir este tipo de atendimento?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sim, excluir",
      cancelButtonText: "Cancelar",
    });

    if (result.isConfirmed) {
      setLoading(true);
      try {
        await appointmentTypesService.deleteAppointmentType(id);
        Swal.fire({
          title: "Sucesso",
          text: "Tipo de atendimento removido com sucesso",
          icon: "success",
        });
        fetchAppointmentTypes(currentPage, perPage, filterStatus, searchTerm);
      } catch (error) {
        Swal.fire({
          title: "Erro",
          text: "Não foi possível remover o tipo de atendimento",
          icon: "error",
        });
      } finally {
        setLoading(false);
      }
    }
  }, [currentPage, perPage, filterStatus, searchTerm, fetchAppointmentTypes]);

  // Alternar status ativo/inativo
  const toggleActive = useCallback(async (id, e) => {
    e.stopPropagation();
    setLoading(true);
    try {
      await appointmentTypesService.toggleActiveStatus(id);
      fetchAppointmentTypes(currentPage, perPage, filterStatus, searchTerm);
    } catch (error) {
      Swal.fire({
        title: "Erro",
        text: "Não foi possível alterar o status",
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [currentPage, perPage, filterStatus, searchTerm, fetchAppointmentTypes]);

  // Função para lidar com mudança de página
  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
    fetchAppointmentTypes(page, perPage, filterStatus, searchTerm);
  }, [perPage, filterStatus, searchTerm, fetchAppointmentTypes]);

  // Configuração para filtros de busca
  const filterByField = {
    fields: ["name", "description"],
    labels: ["Nome", "Descrição"],
  };

  const getStats = () => {
    const types = appointmentTypes || [];
    const total = types.length;
    const active = types.filter((t) => t && t.is_active).length;
    const inactive = total - active;
    const avgDuration =
      total > 0
        ? Math.round(
            types.reduce((acc, t) => acc + (t?.duration || 0), 0) / total
          )
        : 0;

    return { total, active, inactive, avgDuration };
  };

  const stats = getStats();

  // Definir as colunas da tabela
  const columns = useMemo(
    () => [
      {
        name: "Nome",
        selector: (row) => row.name,
        sortable: true,
        cell: (row) => (
          <div>
            <div style={{ fontWeight: "600", color: "var(--text-primary)" }}>
              {row.name}
            </div>
            {row.description && (
              <div style={{ fontSize: "12px", color: "var(--text-secondary)" }}>
                {row.description}
              </div>
            )}
          </div>
        ),
        grow: 2,
      },
      {
        name: "Agenda",
        selector: (row) => row.calendar?.name || "Padrão",
        sortable: true,
        cell: (row) => (
          <span style={{ fontSize: "14px" }}>
            {row.calendar?.name || <em>Padrão</em>}
          </span>
        ),
        width: "140px",
      },
      {
        name: "Duração",
        selector: (row) => row.duration,
        sortable: true,
        cell: (row) => (
          <div className="">
           
            {row.duration}min
          </div>
        ),
        width: "100px",
      },
      {
        name: "Valor",
        selector: (row) => row.price,
        sortable: true,
        cell: (row) => (
          <span style={{ fontSize: "14px" }}>
            {row.price ? `R$ ${parseFloat(row.price).toFixed(2)}` : "-"}
          </span>
        ),
        width: "100px",
      },
      {
        name: "Cor",
        selector: (row) => row.color,
        cell: (row) => (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <div
              className="atm-color-sample"
              style={{ backgroundColor: row.color }}
            />
            {/* <span style={{ fontSize: "12px", color: "#6c757d" }}>
              {row.color}
            </span> */}
          </div>
        ),
        width: "100px",
      },
      {
        name: "Status",
        selector: (row) => row.is_active,
        sortable: true,
        cell: (row) => (
          <span
            className={`atm-status-badge ${row.is_active ? "active" : "inactive"}`}
          >
            {row.is_active ? "Ativo" : "Inativo"}
          </span>
        ),
        width: "100px",
      },
      {
        name: "Ações",
        cell: (row) => (
          <div style={{ display: "flex", gap: "4px", justifyContent: "flex-end" }}>
            <Button
              variant={row.is_active ? "warning" : "success"}
              size="sm"
              onClick={(e) => toggleActive(row.id, e)}
              title={row.is_active ? "Desativar" : "Ativar"}
              style={{ padding: "4px 6px" }}
            >
              {row.is_active ? (
                <IconToggleRight size={14} />
              ) : (
                <IconToggleLeft size={14} />
              )}
            </Button>

            <Button
              variant="primary"
              size="sm"
              onClick={(e) => handleEdit(row, e)}
              title="Editar"
              style={{ padding: "4px 6px" }}
            >
              <IconEdit size={14} />
            </Button>

            <Button
              variant="danger"
              size="sm"
              onClick={(e) => handleDelete(row.id, e)}
              title="Excluir"
              style={{ padding: "4px 6px" }}
            >
              <IconTrash size={14} />
            </Button>
          </div>
        ),
        width: "140px",
        ignoreRowClick: true,
      },
    ],
    [toggleActive, handleEdit, handleDelete]
  );

  if (loading && appointmentTypes.length === 0) {
    return (
      <div className="atm-container">
        <div className="atm-loading-container">
          <Spinner animation="border" variant="success" />
          <p className="mt-3">Carregando tipos de atendimento...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="atm-container">
      <div className="atm-header">
        <div className="atm-header-content">
          <h1 className="atm-title">
            <span className="atm-title-icon">
              <IconCalendarEvent size={24} />
            </span>
            Tipos de Atendimento
          </h1>
          <Button variant="outline-primary" onClick={handleOpenSettings}>
            <IconSettings size={18} className="me-1" />
            <span className="d-md-inline">Configurações Globais</span>
          </Button>
        </div>
      </div>

      {/* Informações sobre configurações atuais */}
      {settings && (
        <InfoCard
          style={{
            background: "linear-gradient(135deg, #e8f5e8, #f8f9fa)",
            borderLeft: "4px solid #28a745",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: "12px",
            }}
          >
            <p style={{ margin: 0, flex: 1 }}>
              <strong>Configurações dos slots na agenda:</strong> {" "}
              {settings.appointment_duration} minutos
              {settings.appointment_interval > 0 &&
                ` com ${settings.appointment_interval} minutos de intervalo entre atendimentos`}
            </p>
            <Button
              variant="link"
              size="sm"
              onClick={handleOpenSettings}
              style={{
                padding: "4px 8px",
                textDecoration: "none",
                fontWeight: "600",
              }}
            >
              <IconEdit size={14} className="me-1" />
              Alterar
            </Button>
          </div>
        </InfoCard>
      )}

      <InfoCard>
        <p>
          Configure os tipos de atendimento disponíveis e suas respectivas
          durações. Cada tipo pode ter uma duração específica em minutos e uma
          cor para identificação visual na agenda.
        </p>
      </InfoCard>

      <SearchContainer>
        <SearchIcon>
          <IconSearch size={18} />
        </SearchIcon>
        <SearchInput
          type="text"
          placeholder="Buscar tipos de atendimento..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </SearchContainer>

      <div className="atm-stats-container atm-stats-mobile">
        <div className="atm-stat-card">
          <div className="atm-stat-number">{stats.total}</div>
          <div className="atm-stat-label">Total</div>
        </div>
        <div className="atm-stat-card">
          <div className="atm-stat-number">{stats.active}</div>
          <div className="atm-stat-label">Ativos</div>
        </div>
        <div className="atm-stat-card">
          <div className="atm-stat-number">{stats.inactive}</div>
          <div className="atm-stat-label">Inativos</div>
        </div>
        <div className="atm-stat-card">
          <div className="atm-stat-number">{stats.avgDuration}min</div>
          <div className="atm-stat-label">Duração Média</div>
        </div>
      </div>

      {/* DataTable wrapper responsivo */}
      <div className="atm-mobile-container">
        <div className="atm-datatable-wrapper">
          <div style={{ marginBottom: "20px", display: "flex", gap: "12px", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
              <Button 
                variant={filterStatus === "all" ? "primary" : "outline-secondary"}
                size="sm"
                onClick={() => setFilterStatus("all")}
              >
                <IconFilter size={16} className="me-1" />
                Todos
              </Button>
              <Button 
                variant={filterStatus === "active" ? "success" : "outline-success"}
                size="sm"
                onClick={() => setFilterStatus("active")}
              >
                Ativos
              </Button>
              <Button 
                variant={filterStatus === "inactive" ? "warning" : "outline-warning"}
                size="sm"
                onClick={() => setFilterStatus("inactive")}
              >
                Inativos
              </Button>
            </div>
            <Button 
              variant="primary"
              size="sm"
              onClick={handleAddNew}
              className="atm-add-button-desktop"
            >
              <IconPlus size={16} className="me-1" />
              Novo Tipo
            </Button>
          </div>

          {appointmentTypes.length === 0 && !loading ? (
            <div className="atm-empty-state">
              <div className="atm-empty-icon">📅</div>
              <h3>
                {searchTerm ? "Nenhum tipo encontrado" : "Nenhum tipo configurado"}
              </h3>
              <p>
                {searchTerm
                  ? "Tente ajustar sua busca para encontrar tipos de atendimento."
                  : "Comece criando seus primeiros tipos de atendimento para organizar melhor seus agendamentos."}
              </p>
              {!searchTerm && (
                <Button variant="primary" onClick={handleAddNew}>
                  Criar Primeiro Tipo
                </Button>
              )}
            </div>
          ) : (
            <DatatablePaginated
              data={appointmentTypes}
              columns={columns}
              totalRows={totalRows}
              isLoading={loading}
              handlePageChange={handlePageChange}
              filterByField={filterByField}
              filterDefaultField="name"
              searchBarType={1}
              classes="px-0"
            />
          )}
        </div>

        {/* Mobile Cards */}
        <div className="atm-mobile-cards">
          {loading ? (
            <div className="atm-mobile-loader">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3">Carregando...</p>
            </div>
          ) : appointmentTypes.length === 0 ? (
            <div className="atm-mobile-empty">
              <div className="atm-empty-icon">📅</div>
              <h3>
                {searchTerm ? "Nenhum tipo encontrado" : "Nenhum tipo configurado"}
              </h3>
              <p>
                {searchTerm
                  ? "Tente ajustar sua busca para encontrar tipos de atendimento."
                  : "Comece criando seus primeiros tipos de atendimento para organizar melhor seus agendamentos."}
              </p>
              {!searchTerm && (
                <Button variant="primary" onClick={handleAddNew}>
                  Criar Primeiro Tipo
                </Button>
              )}
            </div>
          ) : (
            <>
              {appointmentTypes.map((type) => (
                <div key={type.id} className="atm-card">
                  {/* Header do card */}
                  <div className="atm-card-header">
                    <div className="atm-type-info">
                      <h4 className="atm-type-name">
                        <div
                          className="atm-color-sample-mobile"
                          style={{ backgroundColor: type.color }}
                        />
                        {type.name}
                      </h4>
                      {type.description && (
                        <p className="atm-type-description">{type.description}</p>
                      )}
                    </div>
                    <span
                      className={`atm-card-status-badge ${type.is_active ? "active" : "inactive"}`}
                    >
                      {type.is_active ? "Ativo" : "Inativo"}
                    </span>
                  </div>

                  {/* Body do card */}
                  <div className="atm-card-body">
                    <div className="atm-info-row">
                      <span className="atm-info-label">Agenda:</span>
                      <span className="atm-info-value atm-calendar-mobile">
                        {type.calendar?.name || "Padrão"}
                      </span>
                    </div>
                    <div className="atm-info-row">
                      <span className="atm-info-label">Duração:</span>
                      <span className="atm-info-value">
                        <div className="atm-duration-mobile">
                          <IconClock size={14} />
                          {type.duration}min
                        </div>
                      </span>
                    </div>
                    <div className="atm-info-row">
                      <span className="atm-info-label">Valor:</span>
                      <span className="atm-info-value atm-price-mobile">
                        {type.price ? `R$ ${parseFloat(type.price).toFixed(2)}` : "-"}
                      </span>
                    </div>
                    <div className="atm-info-row">
                      <span className="atm-info-label">Cor:</span>
                      <span className="atm-info-value">
                        <div className="atm-color-info-mobile">
                          <span className="atm-color-code-mobile">{type.color}</span>
                        </div>
                      </span>
                    </div>
                  </div>

                  {/* Actions do card */}
                  <div className="atm-card-actions">
                    <Button
                      size="sm"
                      variant={type.is_active ? "warning" : "success"}
                      onClick={(e) => toggleActive(type.id, e)}
                      className="atm-action-btn-mobile"
                      title={type.is_active ? "Desativar" : "Ativar"}
                    >
                      {type.is_active ? (
                        <IconToggleRight size={14} />
                      ) : (
                        <IconToggleLeft size={14} />
                      )}
                      {type.is_active ? "Desativar" : "Ativar"}
                    </Button>

                    <Button
                      size="sm"
                      variant="primary"
                      onClick={(e) => handleEdit(type, e)}
                      className="atm-action-btn-mobile"
                    >
                      <IconEdit size={14} />
                      Editar
                    </Button>

                    <Button
                      size="sm"
                      variant="danger"
                      onClick={(e) => handleDelete(type.id, e)}
                      className="atm-action-btn-mobile"
                    >
                      <IconTrash size={14} />
                      Excluir
                    </Button>
                  </div>
                </div>
              ))}

              {/* Paginação mobile */}
              <div className="atm-mobile-pagination">
                <div className="atm-pagination-info">
                  Página {currentPage} de {Math.ceil(totalRows / perPage)}
                </div>
                <div className="atm-pagination-buttons">
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={currentPage === 1}
                    onClick={() => handlePageChange(currentPage - 1)}
                    className="atm-pagination-btn"
                  >
                    Anterior
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={currentPage >= Math.ceil(totalRows / perPage)}
                    onClick={() => handlePageChange(currentPage + 1)}
                    className="atm-pagination-btn"
                  >
                    Próxima
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <button className="atm-floating-button" onClick={handleAddNew} title="Novo Tipo de Atendimento">
        <IconPlus size={24} />
      </button>

      {/* Modal de configurações globais */}
      <Modal
        show={showSettingsModal}
        onHide={() => setShowSettingsModal(false)}
        size="md"
        centered
      >
        <form onSubmit={handleSubmitSettings(onSubmitSettings)}>
          <Modal.Header closeButton>
            <Modal.Title>
              <IconSettings size={20} className="me-2" />
              Configurações Globais
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div
              style={{
                background: "#f8f9fa",
                padding: "16px",
                borderRadius: "8px",
                marginBottom: "20px",
                border: "1px solid #e9ecef",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  marginBottom: "8px",
                }}
              >
                <IconAlertCircle size={16} color="#17a2b8" />
                <strong style={{ color: "#17a2b8", fontSize: "14px" }}>
                  Informação Importante
                </strong>
              </div>
              <p
                style={{
                  margin: 0,
                  fontSize: "14px",
                  color: "#495057",
                  lineHeight: "1.4",
                }}
              >
                Essas configurações servem como referência para os slots de
                tempo da agenda.
              </p>
            </div>

            <Form.Group>
              <Label>
                Duração Padrão (minutos){" "}
                <span style={{ color: "#dc3545" }}>*</span>
              </Label>
              <Input
                type="number"
                min="1"
                placeholder="30"
                {...registerSettings("appointment_duration")}
                className={
                  errorsSettings.appointment_duration ? "is-invalid" : ""
                }
              />
              {errorsSettings.appointment_duration && (
                <ErrorMessage>
                  {errorsSettings.appointment_duration.message}
                </ErrorMessage>
              )}
              <HelpText>
                Duração padrão em minutos para novos tipos de atendimento.
              </HelpText>
            </Form.Group>

            <Form.Group className="mt-2">
              <Label>
                Intervalo Entre Atendimentos (minutos){" "}
                <span style={{ color: "#dc3545" }}>*</span>
              </Label>
              <Input
                type="number"
                min="0"
                placeholder="0"
                {...registerSettings("appointment_interval")}
                className={
                  errorsSettings.appointment_interval ? "is-invalid" : ""
                }
              />
              {errorsSettings.appointment_interval && (
                <ErrorMessage>
                  {errorsSettings.appointment_interval.message}
                </ErrorMessage>
              )}
              <HelpText>
                Tempo adicional entre atendimentos. Use 0 para não ter
                intervalo.
              </HelpText>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowSettingsModal(false)}
            >
              Cancelar
            </Button>
            <Button variant="primary" type="submit" disabled={loading}>
              {loading ? (
                <Spinner animation="border" size="sm" className="me-1" />
              ) : (
                <IconCheck size={16} className="me-1" />
              )}
              Salvar Configurações
            </Button>
          </Modal.Footer>
        </form>
      </Modal>

      {/* Modal de adição/edição */}
      <Modal
        show={showModal}
        onHide={() => {
          setShowModal(false);
          setShowColorPicker(false);
        }}
        size="lg"
        centered
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <Modal.Header closeButton>
            <Modal.Title>
              <IconCalendarEvent size={20} className="me-2" />
              {editingId
                ? "Editar Tipo de Atendimento"
                : "Novo Tipo de Atendimento"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {/* Campo Nome - Linha completa */}
            <Row className="mb-3">
              <Col xs={12}>
                <Form.Group>
                  <Form.Label>
                    Nome <span style={{ color: "#dc3545" }}>*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Ex: Consulta Inicial"
                    {...register("name")}
                    isInvalid={!!errors.name}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.name?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
            {/* Campo Agenda - Linha completa */}
            <Row className="mb-3">
              <Col xs={12}>
                <Form.Group>
                  <Form.Label>
                    Agenda <span style={{ color: "#dc3545" }}>*</span>
                  </Form.Label>
                  <Form.Control
                    as="select"
                    {...register("calendar")}
                    isInvalid={!!errors.calendar}
                  >
                    <option value="">Selecione uma agenda...</option>
                    {calendars.map((calendar) => (
                      <option key={calendar.id} value={calendar.id}>
                        {calendar.name}
                      </option>
                    ))}
                  </Form.Control>
                  <Form.Control.Feedback type="invalid">
                    {errors.calendar?.message}
                  </Form.Control.Feedback>
                  <Form.Text className="text-muted">
                    Selecione a agenda onde este tipo de atendimento será
                    usado.
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            {/* Duração e Valor - Lado a lado */}
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>
                    Duração (minutos){" "}
                    <span style={{ color: "#dc3545" }}>*</span>
                  </Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    {...register("duration")}
                    isInvalid={!!errors.duration}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.duration?.message}
                  </Form.Control.Feedback>
                  <Form.Text className="text-muted">
                    Tempo em minutos que este tipo de atendimento ocupa no
                    agenda.
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Valor</Form.Label>
                  <Controller
                    control={control}
                    name="price"
                    rules={{ required: false }}
                    render={({ field }) => (
                      <NumberFormat
                        fixedDecimalScale={true}
                        decimalScale={2}
                        thousandSeparator="."
                        decimalSeparator=","
                        prefix="R$"
                        className={`form-control ${
                          errors.price ? "is-invalid" : ""
                        }`}
                        {...field}
                      />
                    )}
                  />
                  {errors.price && (
                    <div className="invalid-feedback d-block">
                      {errors.price.message}
                    </div>
                  )}
                </Form.Group>
              </Col>
            </Row>

            {/* Campo Descrição - Linha completa */}
            <Row className="mb-3">
              <Col xs={12}>
                <Form.Group>
                  <Form.Label>Descrição (opcional)</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    placeholder="Descrição deste tipo de atendimento"
                    {...register("description")}
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Campo Cor - Linha completa */}
            <Row className="mb-3">
              <Col xs={12}>
                <Form.Group>
                  <Form.Label>
                    Cor <span style={{ color: "#dc3545" }}>*</span>
                  </Form.Label>
                  <ColorInputContainer>
                    <Form.Control
                      type="text"
                      value={watchedColor}
                      onChange={(e) => setValue("color", e.target.value)}
                      isInvalid={!!errors.color}
                      style={{ flex: 1, width: "80%" }}
                    />
                    <ColorButton
                      type="button"
                      onClick={() => setShowColorPicker(!showColorPicker)}
                    >
                      <div
                        style={{
                          backgroundColor: watchedColor,
                          width: "20px",
                          height: "20px",
                          borderRadius: "4px",
                        }}
                      />
                      <IconPalette size={16} />
                    </ColorButton>
                  </ColorInputContainer>
                  {errors.color && (
                    <div className="invalid-feedback d-block">
                      {errors.color.message}
                    </div>
                  )}
                  {showColorPicker && (
                    <ColorPickerContainer ref={pickerRef}>
                      <SketchPicker
                        color={watchedColor}
                        onChange={(color) => setValue("color", color.hex)}
                      />
                    </ColorPickerContainer>
                  )}
                </Form.Group>
              </Col>
            </Row>

            {/* Switch Ativo - Linha completa */}
            <Row className="mb-2">
              <Col xs={12}>
                <Form.Group>
                  <SwitchContainer>
                    <CustomSwitch
                      checked={watchedIsActive}
                      onClick={() => setValue("is_active", !watchedIsActive)}
                    />
                    <Label>Ativo</Label>
                  </SwitchContainer>
                  <Form.Text className="text-muted">
                    Desative para ocultar temporariamente este tipo de
                    atendimento.
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => {
                setShowModal(false);
                setShowColorPicker(false);
              }}
            >
              Cancelar
            </Button>
            <Button variant="primary" type="submit" disabled={loading}>
              {loading ? (
                <Spinner animation="border" size="sm" className="me-1" />
              ) : (
                <IconCheck size={16} className="me-1" />
              )}
              {editingId ? "Atualizar" : "Adicionar"}
            </Button>
          </Modal.Footer>
        </form>
      </Modal>
    </div>
  );
};

export default AppointmentTypesManager;
